import React, { useMemo } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import styled from "styled-components";
import { Container } from "./styles";
import {
  computeAreaFromCorners,
  computePlayerCuboPa,
  computeProjectCuboPa,
} from "../utils/cuboPricing";

const Panel = styled(Box)`
  width: 100%;
  max-width: 520px;
  margin-top: 16px;
  padding: 20px 24px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
`;

function parseIntegerLoose(s) {
  if (s == null) return null;
  const t = String(s).trim().replace(/\s/g, "").replace(",", ".");
  if (t === "" || t === "-" || t === "+") return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
}

function formatPaFr(value) {
  return new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

const CuboPricingCalculator = () => {
  const [inputMode, setInputMode] = React.useState("area");
  const [cuboKind, setCuboKind] = React.useState("project");

  const [areaStr, setAreaStr] = React.useState("");
  const [lenStr, setLenStr] = React.useState("");
  const [widStr, setWidStr] = React.useState("");
  const [x1Str, setX1Str] = React.useState("");
  const [z1Str, setZ1Str] = React.useState("");
  const [x2Str, setX2Str] = React.useState("");
  const [z2Str, setZ2Str] = React.useState("");

  const resolvedArea = useMemo(() => {
    if (inputMode === "area") {
      const a = parseIntegerLoose(areaStr);
      if (a == null || a < 1) return null;
      return a;
    }
    if (inputMode === "dimensions") {
      const l = parseIntegerLoose(lenStr);
      const w = parseIntegerLoose(widStr);
      if (l == null || w == null || l < 1 || w < 1) return null;
      return l * w;
    }
    const x1 = parseIntegerLoose(x1Str);
    const z1 = parseIntegerLoose(z1Str);
    const x2 = parseIntegerLoose(x2Str);
    const z2 = parseIntegerLoose(z2Str);
    if ([x1, z1, x2, z2].some((v) => v == null)) return null;
    return computeAreaFromCorners(x1, z1, x2, z2);
  }, [inputMode, areaStr, lenStr, widStr, x1Str, z1Str, x2Str, z2Str]);

  const pa =
    resolvedArea != null
      ? cuboKind === "project"
        ? computeProjectCuboPa(resolvedArea)
        : computePlayerCuboPa(resolvedArea)
      : null;

  const belowMinSize = resolvedArea != null && resolvedArea < 100;

  return (
    <Container>
      <h1>Calculateur de cubos</h1>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, textAlign: "center", mb: 1 }}>
        Estimation du coût en PA pour un cuboïde de projet (
        <Link
          href="https://www.minefield.fr/forum/topic/65338-changement-de-r%C3%A8gles-cubos-de-projets-et-joueurs/"
          target="_blank"
          rel="noopener noreferrer"
          color="primary"
        >
          grille tarifaire Minefield
        </Link>
        ) ou un cuboïde personnel (0,50&nbsp;PA par bloc).
      </Typography>

      <Panel>
        <FormControl component="fieldset" sx={{ width: "100%" }}>
          <FormLabel component="legend">Type de cubo</FormLabel>
          <RadioGroup
            row
            value={cuboKind}
            onChange={(e) => setCuboKind(e.target.value)}
          >
            <FormControlLabel value="project" control={<Radio />} label="Cuboïde de projet" />
            <FormControlLabel value="player" control={<Radio />} label="Cuboïde de joueur" />
          </RadioGroup>
        </FormControl>

        <FormControl component="fieldset" sx={{ width: "100%", mt: 2 }}>
          <FormLabel component="legend">Données</FormLabel>
          <RadioGroup
            value={inputMode}
            onChange={(e) => setInputMode(e.target.value)}
          >
            <FormControlLabel value="area" control={<Radio />} label="Aire (nombre de blocs)" />
            <FormControlLabel
              value="dimensions"
              control={<Radio />}
              label="Longueur × largeur (blocs)"
            />
            <FormControlLabel
              value="corners"
              control={<Radio />}
              label="Deux coins X / Z (inclusifs)"
            />
          </RadioGroup>
        </FormControl>

        {inputMode === "area" && (
          <TextField
            label="Aire"
            type="text"
            inputMode="numeric"
            value={areaStr}
            onChange={(e) => setAreaStr(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            helperText="Surface totale en blocs protégés."
          />
        )}

        {inputMode === "dimensions" && (
          <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap" }}>
            <TextField
              label="Longueur"
              type="text"
              inputMode="numeric"
              value={lenStr}
              onChange={(e) => setLenStr(e.target.value)}
              sx={{ flex: "1 1 140px" }}
            />
            <TextField
              label="Largeur"
              type="text"
              inputMode="numeric"
              value={widStr}
              onChange={(e) => setWidStr(e.target.value)}
              sx={{ flex: "1 1 140px" }}
            />
          </Box>
        )}

        {inputMode === "corners" && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Coin 1
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <TextField
                label="X₁"
                type="text"
                inputMode="numeric"
                value={x1Str}
                onChange={(e) => setX1Str(e.target.value)}
                sx={{ flex: "1 1 120px" }}
              />
              <TextField
                label="Z₁"
                type="text"
                inputMode="numeric"
                value={z1Str}
                onChange={(e) => setZ1Str(e.target.value)}
                sx={{ flex: "1 1 120px" }}
              />
            </Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Coin 2
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="X₂"
                type="text"
                inputMode="numeric"
                value={x2Str}
                onChange={(e) => setX2Str(e.target.value)}
                sx={{ flex: "1 1 120px" }}
              />
              <TextField
                label="Z₂"
                type="text"
                inputMode="numeric"
                value={z2Str}
                onChange={(e) => setZ2Str(e.target.value)}
                sx={{ flex: "1 1 120px" }}
              />
            </Box>
            <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
              Les deux blocs de coin sont inclus (largeur = |X₁−X₂|+1, idem pour Z).
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 3, p: 2, borderRadius: 1, bgcolor: "action.hover" }}>
          {resolvedArea != null && pa != null ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Aire retenue : {resolvedArea.toLocaleString("fr-FR")} blocs
              </Typography>
              <Typography variant="h5" component="p" sx={{ mt: 1 }}>
                {formatPaFr(pa)} PA
              </Typography>
            </>
          ) : (
            <Typography color="text.secondary">Saisissez des valeurs valides pour voir le prix.</Typography>
          )}
        </Box>

        {belowMinSize && (
          <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
            Rappel : la taille minimale annoncée pour un cuboïde est de 10×10 blocs (100 blocs).
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          Pour un <strong>cuboïde de projet</strong>, envoyez un message privé sur le forum à la
          modération avec les informations requises. Pour un <strong>cuboïde personnel</strong>,
          ouvrez un ticket en jeu.
        </Typography>
      </Panel>
    </Container>
  );
};

export default CuboPricingCalculator;
